import React from "react";
import { Calendar, TrendingUp, Users, Palette, ArrowRight, Check, Download } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  heroData,
  problemData,
  solutionData,
  featuresData,
  pricingData,
  footerData,
} from "../mockData";

const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.paymaplabs.paymap";

const iconMap = {
  Calendar: Calendar,
  TrendingUp: TrendingUp,
  Users: Users,
  Palette: Palette
};

const Landing = () => {

  const handleScroll = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="nav-header">
        <div className="nav-content">
          <div className="nav-logo" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src={`${process.env.PUBLIC_URL}/PayMap.ico`} alt="PayMap" style={{ width: 36, height: 36, borderRadius: 8 }} />
            <span className="logo-text"><span style={{ color: '#1F2937' }}>Pay</span><span style={{ color: '#2563EB' }}>Map</span></span>
          </div>
          <div className="nav-links">
            <button onClick={() => handleScroll("features")} className="nav-link">
              Features
            </button>
            <button onClick={() => handleScroll("pricing")} className="nav-link">
              Pricing
            </button>
            <Button onClick={() => handleScroll("download")} className="btn-primary btn-sm">
              Download
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content-wrapper">
          <div className="hero-content">
            <h1 className="hero-title">{heroData.headline}</h1>
            <p className="hero-subtitle">{heroData.subheadline}</p>
            <div className="hero-cta">
              <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                <Button className="btn-primary btn-lg">
                  Download on Google Play
                  <Download className="ml-2" size={20} />
                </Button>
              </a>
              <Button onClick={() => handleScroll("how-it-works")} className="btn-secondary btn-lg">
                {heroData.secondaryCTA}
              </Button>
            </div>
          </div>
          <div className="hero-animation">
            <video 
              className="hero-video"
              autoPlay 
              loop 
              muted 
              playsInline
            >
              <source 
                src={`${process.env.PUBLIC_URL}/landing page mobile animation.mp4`}
                type="video/mp4"
              />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="problem-section section-spacing">
        <div className="container">
          <h2 className="section-title">{problemData.headline}</h2>
          <div className="problem-list">
            {problemData.problems.map((problem, index) => (
              <div key={index} className="problem-item">
                <div className="problem-bullet"></div>
                <p className="body-large">{problem}</p>
              </div>
            ))}
          </div>
          <p className="problem-closing body-large">{problemData.closing}</p>
        </div>
      </section>

      {/* Solution Section */}
      <section id="how-it-works" className="solution-section section-spacing">
        <div className="container">
          <h2 className="section-title">{solutionData.headline}</h2>
          <ul className="solution-list">
            {solutionData.benefits.map((benefit, index) => (
              <li key={index} className="solution-item">
                <Check className="solution-check" size={24} />
                <span className="body-large">{benefit}</span>
              </li>
            ))}
          </ul>
          <div className="solution-features">
            {solutionData.features.map((feature, index) => (
              <p key={index} className="solution-feature-text">
                {feature}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* Product Demo Section */}
      <section className="demo-section section-spacing">
        <div className="container">
          <h2 className="section-title">See PayMap in Action</h2>
          <p className="demo-subtitle body-large">
            A clear, visual way to manage your bi-weekly paycheck cycle.
          </p>
          
          <div className="demo-grid">
            <div className="demo-item">
              <div className="demo-image-wrapper">
                <img 
                  src={`${process.env.PUBLIC_URL}/Calendar grid 2.png`} 
                  alt="PayMap calendar view showing bills mapped to paycheck dates"
                  className="demo-image"
                />
              </div>
              <div className="demo-caption">
                <h3 className="demo-caption-title">Paycheck-Aligned Calendar</h3>
                <p className="demo-caption-text">
                  See exactly which bills fall between your paychecks. Weekly net totals show you red or green—so you always know where you stand.
                </p>
              </div>
            </div>

            <div className="demo-item">
              <div className="demo-image-wrapper">
                <img 
                  src="https://customer-assets.emergentagent.com/job_payday-aligned/artifacts/yn9gxu0i_Screenshot%202026-02-16%20124511.png" 
                  alt="PayMap category breakdown with visual spending analysis"
                  className="demo-image"
                />
              </div>
              <div className="demo-caption">
                <h3 className="demo-caption-title">Category Breakdown</h3>
                <p className="demo-caption-text">
                  Understand where your money goes with clear category tracking and visual breakdowns. See your net savings at a glance.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section section-spacing">
        <div className="container">
          <h2 className="section-title">Feature Highlights</h2>
          <div className="features-grid">
            {featuresData.map((feature) => {
              const IconComponent = iconMap[feature.icon];
              return (
                <div key={feature.id} className="feature-card">
                  <div className="feature-icon">
                    <IconComponent size={32} />
                  </div>
                  <h3 className="feature-title">{feature.title}</h3>
                  <p className="feature-description">{feature.description}</p>
                </div>
              );
            })}
          </div>

          {/* Weekly Net Tracking Visual Highlight */}
          <div className="feature-visual-highlight">
            <div className="feature-visual-content">
              <div className="feature-visual-text">
                <h3 className="feature-visual-title">Weekly Net Tracking</h3>
                <p className="feature-visual-description">
                  Know instantly if this week runs negative. See red or green each week so you always know where you stand.
                </p>
                <ul className="feature-visual-list">
                  <li>
                    <Check size={20} className="feature-visual-check" />
                    <span>Week-by-week income and bill breakdown</span>
                  </li>
                  <li>
                    <Check size={20} className="feature-visual-check" />
                    <span>Instant visual feedback with green net totals</span>
                  </li>
                  <li>
                    <Check size={20} className="feature-visual-check" />
                    <span>Plan ahead and avoid financial stress</span>
                  </li>
                </ul>
              </div>
              <div className="feature-visual-image-wrapper">
                <img 
                  src="https://customer-assets.emergentagent.com/job_payday-aligned/artifacts/11ck1tnr_Desktop%20-%201.png"
                  alt="Weekly Net Tracking showing income, bills, and net totals for multiple weeks"
                  className="feature-visual-image"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing-section section-spacing">
        <div className="container">
          <div className="pricing-card">
            <h2 className="pricing-title">{pricingData.title}</h2>
            <div className="pricing-amount">
              <span className="price">{pricingData.price}</span>
              <span className="price-note">{pricingData.priceNote}</span>
            </div>
            <ul className="pricing-features">
              {pricingData.features.map((feature, index) => (
                <li key={index} className="pricing-feature">
                  <Check className="pricing-check" size={20} />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
            <p className="pricing-notice">{pricingData.notice}</p>
            <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
              <Button className="btn-primary btn-full">
                Download Now
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Download Section */}
      <section id="download" className="signup-section section-spacing">
        <div className="container">
          <div className="signup-content">
            <h2 className="section-title">Get PayMap</h2>
            <p className="body-large signup-subtitle">
              Download PayMap and take control of your paycheck-to-paycheck budgeting.
            </p>
            <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
              <Button className="btn-primary btn-lg">
                <Download size={20} style={{ marginRight: 8 }} />
                Get it on Google Play
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <h3 className="footer-logo"><span style={{ color: '#f1f5f9' }}>Pay</span><span style={{ color: '#3b82f6' }}>Map</span></h3>
              <p className="footer-tagline">{footerData.tagline}</p>
            </div>
            <div className="footer-links">
              {footerData.links.map((link, index) => (
                <a key={index} href={link.href} className="footer-link">
                  {link.label}
                </a>
              ))}
            </div>
          </div>
          <div className="footer-bottom">
            <p className="footer-copyright">{footerData.copyright}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
